import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, AlertCircle } from 'lucide-react';

/**
 * RichTextEditor - A robust rich text editor with fallback
 * Uses Tiptap (React 18/19 compatible) with automatic fallback to textarea
 */

// Toolbar button component
function ToolbarButton({ onClick, isActive, disabled, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded hover:bg-gray-100 transition-colors ${
        isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

// Tiptap Editor Component
function TiptapEditor({ value, onChange, placeholder }) {
  // Track if we've set initial content to avoid overwriting user edits
  const hasSetInitialContent = useRef(false);
  const lastExternalValue = useRef(null);  // Start with null to force initial update
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable built-in list extensions so we can use explicit ones
        bulletList: false,
        orderedList: false,
        listItem: false,
        heading: {
          levels: [2, 3],
        },
      }),
      Underline,
      // Explicitly add list extensions with proper configuration
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc pl-4',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'list-decimal pl-4',
        },
      }),
      ListItem,
    ],
    content: '',  // Start empty, content will be set via useEffect
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Convert empty paragraph to empty string
      if (html === '<p></p>') {
        onChange('');
      } else {
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3',
      },
    },
  });

  // Update editor content when value changes externally (e.g., from API fetch)
  // This is crucial for loading existing course descriptions
  useEffect(() => {
    if (!editor) return;
    
    const normalizedValue = value || '';
    
    // Skip if value hasn't actually changed from last external source
    if (normalizedValue === lastExternalValue.current) return;
    
    // Update the ref to track this value
    lastExternalValue.current = normalizedValue;
    
    const currentContent = editor.getHTML();
    const normalizedCurrent = currentContent === '<p></p>' ? '' : currentContent;
    
    // Only update if content is genuinely different
    // This prevents cursor jumping while user is typing
    if (normalizedCurrent !== normalizedValue) {
      // Use setTimeout to ensure editor is ready
      setTimeout(() => {
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(normalizedValue, false);
          hasSetInitialContent.current = true;
        }
      }, 0);
    }
  }, [value, editor]);

  if (!editor) {
    return <div className="border rounded-md p-3 min-h-[150px] bg-gray-50">Laden...</div>;
  }

  // Handler functions for list toggles with proper focus management
  const handleBulletList = () => {
    editor.chain().focus().toggleBulletList().run();
  };

  const handleOrderedList = () => {
    editor.chain().focus().toggleOrderedList().run();
  };

  return (
    <div className="border rounded-md overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b bg-gray-50 flex-wrap">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Vet (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Cursief (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Onderstrepen (Ctrl+U)"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Kop"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        
        <div className="w-px h-6 bg-gray-300 mx-1" />
        
        <ToolbarButton
          onClick={handleBulletList}
          isActive={editor.isActive('bulletList')}
          title="Opsomming"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={handleOrderedList}
          isActive={editor.isActive('orderedList')}
          title="Genummerde lijst"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
      </div>
      
      {/* Editor Content */}
      <EditorContent editor={editor} />
      
      {/* Placeholder hint */}
      {!value && (
        <style>{`
          .ProseMirror p.is-editor-empty:first-child::before {
            color: #9ca3af;
            content: '${placeholder || 'Voer je tekst in...'}';
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
      )}
    </div>
  );
}

// Fallback Plain Textarea
function PlainTextFallback({ value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>Rich text editor kon niet worden geladen. Je kunt gewone tekst invoeren.</span>
      </div>
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Voer je tekst in...'}
        rows={6}
        className="w-full"
      />
    </div>
  );
}

// Simple Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RichTextEditor] Error caught by boundary:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Main RichTextEditor with error boundary
export default function RichTextEditor({ value, onChange, placeholder }) {
  const [hasError, setHasError] = useState(false);
  const mountedRef = useRef(false);

  // Track if component is mounted (for SSR safety)
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Handle errors from Tiptap
  const handleError = useCallback((error) => {
    console.error('[RichTextEditor] Error:', error);
    setHasError(true);
  }, []);

  // Show fallback if there was an error
  if (hasError) {
    return <PlainTextFallback value={value} onChange={onChange} placeholder={placeholder} />;
  }

  // Render with error boundary
  const fallbackElement = <PlainTextFallback value={value} onChange={onChange} placeholder={placeholder} />;

  return (
    <ErrorBoundary onError={handleError} fallback={fallbackElement}>
      <TiptapEditor value={value} onChange={onChange} placeholder={placeholder} />
    </ErrorBoundary>
  );
}
